import * as Sentry from '@sentry/vue'

import deepMergeObjects from '@/lib/deepMerge'

import { OVERRIDE_NAMESPACES } from './constants'
import { getLocalTranslations } from './translations'

/**
 * @param {Object} translationsByNamespace
 * @returns {Object}
 */
function mergeDevTranslations(translationsByNamespace) {
  const finalTranslations = {}

  Object.values(translationsByNamespace).forEach((nsTranslations) => {
    Object.keys(nsTranslations).forEach((key) => {
      if (typeof nsTranslations[key] === 'object' && nsTranslations[key] !== null) {
        finalTranslations[key] = finalTranslations[key] || {}
        finalTranslations[key] = deepMergeObjects(finalTranslations[key], nsTranslations[key])
      } else {
        finalTranslations[key] = nsTranslations[key]
      }
    })
  })

  return finalTranslations
}

/**
 * @param {Object} baseTranslations
 * @param {Object} overrideTranslationsMap
 * @returns {Object}
 */
function mergeProductionTranslations(baseTranslations, overrideTranslationsMap) {
  const finalTranslations = { ...baseTranslations }

  Object.values(overrideTranslationsMap).forEach((overrideTranslations) => {
    Object.keys(overrideTranslations).forEach((key) => {
      if (typeof overrideTranslations[key] === 'object' && overrideTranslations[key] !== null) {
        if (finalTranslations[key]) {
          finalTranslations[key] = deepMergeObjects(
            finalTranslations[key],
            overrideTranslations[key]
          )
        } else {
          finalTranslations[key] = overrideTranslations[key]
        }
      } else {
        finalTranslations[key] = overrideTranslations[key]
      }
    })
  })

  return finalTranslations
}

/**
 * @param {Object} deps
 * @param {import('vue-i18n').I18n} deps.i18nInstance
 * @param {boolean} deps.isDevMode
 * @param {Set<string>} deps.allNamespaces
 * @param {(lang: string, namespace: string) => Promise<Object>} deps.fetchLocizeNamespace
 * @param {(routePath?: string) => string[]} deps.getActiveOverrideNamespaces
 * @param {() => string} deps.getCurrentRoutePath
 * @param {(locale: string, translations: Object) => void} [deps.cacheLocaleTranslations]
 */
export function createLoadOverrides({
  i18nInstance,
  isDevMode,
  allNamespaces,
  fetchLocizeNamespace,
  getActiveOverrideNamespaces,
  getCurrentRoutePath,
  cacheLocaleTranslations,
}) {
  /**
   * @param {string} lang
   * @param {Object|null} [existingTranslations]
   * @param {string} [routePath]
   */
  return async function loadOverrides(
    lang,
    existingTranslations = null,
    routePath = getCurrentRoutePath()
  ) {
    const activeOverrideNamespaces = getActiveOverrideNamespaces(routePath)

    try {
      const baseTranslations = {}
      const overrideTranslationsMap = {}

      if (existingTranslations && Object.keys(existingTranslations).length > 0) {
        Object.entries(existingTranslations).forEach(([namespace, translations]) => {
          if (!OVERRIDE_NAMESPACES.includes(namespace)) {
            baseTranslations[namespace] = translations
          } else if (activeOverrideNamespaces.includes(namespace)) {
            overrideTranslationsMap[namespace] = translations
          }
        })
      } else {
        const regularNamespaces = [...allNamespaces].filter(
          (ns) => !OVERRIDE_NAMESPACES.includes(ns)
        )

        const regularResults = await Promise.all(
          regularNamespaces.map(async (namespace) => {
            if (!isDevMode) {
              const { data: translations } = await fetchLocizeNamespace(lang, namespace)
              return {
                namespace,
                translations: Object.keys(translations).length > 0 ? translations : null,
              }
            }

            return {
              namespace,
              translations: getLocalTranslations(lang, namespace),
            }
          })
        )

        regularResults.forEach(({ namespace, translations }) => {
          if (translations) {
            baseTranslations[namespace] = translations
          }
        })

        const overrideResults = await Promise.all(
          activeOverrideNamespaces.map(async (namespace) => {
            let override
            if (!isDevMode) {
              const { data: remoteOverride } = await fetchLocizeNamespace(lang, namespace)
              override = remoteOverride

              if (!override || Object.keys(override).length === 0) {
                override = getLocalTranslations(lang, namespace)
              }
            } else {
              override = getLocalTranslations(lang, namespace)
            }

            return { namespace, override }
          })
        )

        overrideResults.forEach(({ namespace, override }) => {
          if (override && Object.keys(override).length > 0) {
            overrideTranslationsMap[namespace] = override
          }
        })

        if (!isDevMode && cacheLocaleTranslations) {
          cacheLocaleTranslations(lang, { ...baseTranslations, ...overrideTranslationsMap })
        }
      }

      const finalTranslations = isDevMode
        ? mergeDevTranslations({
            ...baseTranslations,
            ...overrideTranslationsMap,
          })
        : mergeProductionTranslations(baseTranslations, overrideTranslationsMap)

      i18nInstance.global.setLocaleMessage(lang, finalTranslations)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'i18n' },
        extra: { lang, action: 'loadOverrides' },
      })
      throw error
    }
  }
}
